import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { customersAPI } from "../services/api";
import CustomerModal from "../components/CustomerModal";
import DataTable from "../components/DataTable";
import SearchableSelect from "../components/SearchableSelect";

const NO_CONTACT_OPTIONS = [
  { value: "7",   label: "7+ days" },
  { value: "14",  label: "14+ days" },
  { value: "30",  label: "30+ days" },
  { value: "60",  label: "60+ days" },
];

const NO_ORDER_OPTIONS = [
  { value: "30",  label: "30+ days" },
  { value: "60",  label: "60+ days" },
  { value: "90",  label: "90+ days" },
  { value: "180", label: "180+ days" },
];

const Customers = () => {
  const [customers, setCustomers]         = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState("");
  const [showModal, setShowModal]         = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);

  // API-level filters
  const [filters, setFilters] = useState({
    no_contact_days: "",
    no_order_days:   "",
  });

  /* ── data fetching ───────────────────────────────────────────────── */
  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const params = { ...filters };
      Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
      const response = await customersAPI.getAll(params);
      setCustomers(response.data);
    } catch (err) {
      setError("Failed to load customers");
      console.error("Customers error:", err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  /* ── handlers ────────────────────────────────────────────────────── */
  const handleCreate = ()          => { setEditingCustomer(null); setShowModal(true); };
  const handleEdit   = customer    => { setEditingCustomer(customer); setShowModal(true); };
  const handleClose  = ()          => { setShowModal(false); setEditingCustomer(null); fetchCustomers(); };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this customer? All associated interactions, orders, and notes will also be removed.")) return;
    try {
      await customersAPI.delete(id);
      fetchCustomers();
    } catch {
      alert("Failed to delete customer.");
    }
  };

  /* ── helpers ─────────────────────────────────────────────────────── */
  const formatDate = d => {
    if (!d) return "Never";
    return new Date(d).toLocaleDateString("en-KE");
  };

  const getDaysAgo = d => {
    if (!d) return null;
    return Math.floor((Date.now() - new Date(d).getTime()) / 86_400_000);
  };

  const getStatusBadge = row => {
    if (row.needs_followup && row.needs_order) {
      return (
        <div className="flex flex-wrap gap-1">
          <span className="badge badge-warning text-xs">Needs Follow-up</span>
          <span className="badge badge-danger text-xs">No Recent Orders</span>
        </div>
      );
    }
    if (row.needs_followup) return <span className="badge badge-warning text-xs">Needs Follow-up</span>;
    if (row.needs_order)    return <span className="badge badge-danger text-xs">No Recent Orders</span>;
    return <span className="badge badge-success text-xs">Active</span>;
  };

  /* ── DataTable columns ───────────────────────────────────────────── */
  const columns = [
    {
      key: '_no', label: '#',
      sortable: false, export: false,
      thClass: 'w-12',
      render: (_, __, idx) => (
        <span className="text-gray-400 font-mono text-xs">{idx + 1}</span>
      ),
    },
    {
      key: 'customer_name', label: 'Customer Name',
      exportValue: r => r.customer_name,
      render: (val, row) => (
        <Link
          to={`/customers/${row.id}`}
          className="font-medium text-primary-600 hover:text-primary-800 hover:underline"
        >{val}</Link>
      ),
    },
    {
      key: 'contact_number', label: 'Phone',
      exportValue: r => r.contact_number || r.mobile_number || '',
      render: (_, row) => row.contact_number || row.mobile_number || '-',
    },
    {
      key: 'county', label: 'County',
      exportValue: r => r.county || '',
    },
    {
      key: 'last_contact_date', label: 'Last Contact',
      exportValue: r => formatDate(r.last_contact_date),
      render: (val) => (
        <span className={getDaysAgo(val) > 14 ? 'text-warning-600 font-medium' : ''}>
          {formatDate(val)}
        </span>
      ),
    },
    {
      key: 'last_order_date', label: 'Last Order',
      exportValue: r => formatDate(r.last_order_date),
      render: (val) => (
        <span className={getDaysAgo(val) > 30 ? 'text-danger-600 font-medium' : ''}>
          {formatDate(val)}
        </span>
      ),
    },
    {
      key: 'interaction_count', label: 'Visits',
      exportValue: r => r.interaction_count ?? 0,
      render: val => (
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold">
          {val ?? 0}
        </span>
      ),
    },
    {
      key: 'order_count', label: 'Orders',
      exportValue: r => r.order_count ?? 0,
      render: val => (
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-success-50 text-success-700 text-xs font-semibold">
          {val ?? 0}
        </span>
      ),
    },
    {
      key: 'status', label: 'Status',
      sortable: false, export: false,
      render: (_, row) => getStatusBadge(row),
    },
    {
      key: 'actions', label: 'Actions',
      sortable: false, export: false,
      thClass: 'text-right',
      tdClass: 'text-right',
      render: (_, row) => (
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={() => handleEdit(row)}
            className="btn btn-ghost p-1.5 text-gray-600 hover:text-primary-600"
            title="Edit"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <Link
            to={`/customers/${row.id}`}
            className="btn btn-ghost p-1.5 text-gray-600 hover:text-primary-600"
            title="View Details"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </Link>
          <button
            onClick={() => handleDelete(row.id)}
            className="btn btn-ghost p-1.5 text-red-500 hover:text-red-700"
            title="Delete"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  const searchFields = ['customer_name', 'contact_number', 'mobile_number', 'county', 'email'];

  /* ── render ──────────────────────────────────────────────────────── */
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage your customer database and track interactions
          </p>
        </div>
        <button onClick={handleCreate} className="btn btn-primary inline-flex items-center gap-2 self-start sm:self-auto">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Customer
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchCustomers} className="underline text-sm ml-4">Retry</button>
        </div>
      )}

      {/* DataTable Card */}
      <div className="card">
        <div className="card-body">
          <DataTable
            key={`${filters.no_contact_days}-${filters.no_order_days}`}
            data={customers}
            columns={columns}
            searchFields={searchFields}
            filename="customers"
            title="Customers Report"
            loading={loading}
            rowKey={row => row.id}
          >
            {/* Extra Filters */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pb-2 border-b border-gray-100 mb-2">
              <div>
                <label className="form-label text-xs">No Contact (Days)</label>
                <SearchableSelect
                  value={filters.no_contact_days}
                  onChange={v => setFilters(f => ({ ...f, no_contact_days: v }))}
                  options={NO_CONTACT_OPTIONS}
                  placeholder="All"
                />
              </div>
              <div>
                <label className="form-label text-xs">No Order (Days)</label>
                <SearchableSelect
                  value={filters.no_order_days}
                  onChange={v => setFilters(f => ({ ...f, no_order_days: v }))}
                  options={NO_ORDER_OPTIONS}
                  placeholder="All"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => setFilters({ no_contact_days: '', no_order_days: '' })}
                  className="btn btn-secondary text-sm inline-flex items-center gap-1.5"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Reset Filters
                </button>
              </div>
            </div>
          </DataTable>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <CustomerModal customer={editingCustomer} onClose={handleClose} />
      )}
    </div>
  );
};

export default Customers;
